import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

export interface ChatContainerProps {
  /**
   * Layout variant
   * - card: Wrapped in a Card component with borders/shadow
   * - panel: Simple container for panel views (no card)
   * - fullscreen: Full screen with absolute positioning
   */
  layout?: 'card' | 'panel' | 'fullscreen';

  /**
   * Whether container is in a panel (affects styling)
   */
  isInPanel?: boolean;

  /**
   * Whether container is in a tab
   */
  isInTab?: boolean;

  /**
   * Show border
   */
  showBorder?: boolean;

  /**
   * Additional container className
   */
  className?: string;

  /**
   * Children components
   */
  children: React.ReactNode;
}

export interface ChatContainerMessagesProps {
  children: React.ReactNode;
  className?: string;
  /**
   * Scroll container variant
   * - flex-reverse: flex-col-reverse for bottom-up rendering
   * - normal: standard flex-col
   */
  scrollVariant?: 'flex-reverse' | 'normal';
}

export interface ChatContainerInputProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Unified chat container component
 *
 * Provides consistent layout and styling for different chat contexts:
 * - Card layout for standalone chat views
 * - Panel layout for sidebar chat panels
 * - Fullscreen layout for dedicated chat pages
 */
const ChatContainerComponent: React.FC<ChatContainerProps> = ({
  layout = 'card',
  isInPanel = false,
  isInTab = false,
  showBorder,
  className = '',
  children,
}) => {
  // Determine if we should show borders based on layout and props
  const shouldShowBorder =
    showBorder !== undefined
      ? showBorder
      : layout === 'card' && !isInPanel && !isInTab;

  // Get border classes
  const getBorderClasses = () => {
    if (!shouldShowBorder) return 'border-0';
    if (isInTab) return 'rounded-none border-0';
    if (isInPanel) return '';
    return 'border border-border rounded-none lg:rounded-[20px]';
  };

  // Card layout
  if (layout === 'card') {
    return (
      <div
        className={cn(
          'h-full grid grid-rows-[1fr_auto]',
          isInPanel ? '' : 'bg-background',
          className,
        )}
      >
        <Card
          className={cn(
            'grid grid-rows-[1fr_auto] h-full overflow-hidden transition-all duration-300 ease-in-out',
            isInPanel
              ? 'rounded-none border-0 shadow-none bg-transparent m-0'
              : 'm-0 lg:m-2 lg:mt-0 lg:mb-4 lg:ml-0 lg:mr-2 bg-card shadow-sm',
            getBorderClasses(),
          )}
        >
          <CardContent className="grid grid-rows-[1fr_auto] overflow-hidden p-0">
            {children}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Panel layout
  if (layout === 'panel') {
    return (
      <div className={cn('h-full flex flex-col', className)}>{children}</div>
    );
  }

  // Fullscreen layout with grid
  if (layout === 'fullscreen') {
    return (
      <div
        className={cn(
          'h-full w-full grid grid-rows-[1fr_auto] overflow-hidden',
          className,
        )}
      >
        {children}
      </div>
    );
  }

  return <div className={className}>{children}</div>;
};

// Sub-components for semantic usage
const ChatContainerMessages: React.FC<ChatContainerMessagesProps> = ({
  children,
  className = '',
  scrollVariant = 'normal',
}) => {
  return (
    <div className={cn('overflow-hidden flex flex-col', className)}>
      <div
        className={cn(
          'flex-1 overflow-y-auto overflow-x-hidden',
          scrollVariant === 'flex-reverse' ? 'flex flex-col-reverse' : '',
        )}
        role="log"
      >
        {children}
      </div>
    </div>
  );
};

const ChatContainerInput: React.FC<ChatContainerInputProps> = ({
  children,
  className = '',
}) => {
  // With grid layout, we don't need absolute/sticky positioning
  // The grid row will handle the layout
  return <div className={cn('overflow-hidden', className)}>{children}</div>;
};

// Create compound component with proper TypeScript types
interface ChatContainerType extends React.FC<ChatContainerProps> {
  Messages: typeof ChatContainerMessages;
  Input: typeof ChatContainerInput;
}

// Attach sub-components for compound component pattern
export const ChatContainer = ChatContainerComponent as ChatContainerType;
ChatContainer.Messages = ChatContainerMessages;
ChatContainer.Input = ChatContainerInput;
